
import { SlidersHorizontal, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useTranslation } from 'react-i18next';
import ServiceCategoryFilter from "./ServiceCategoryFilter";
import DomainFilter from "./DomainFilter";
import SubcategoryFilter from "./SubcategoryFilter";
import TherapyModalityFilter from "./TherapyModalityFilter";
import IssueCategoryFilter from "./IssueCategoryFilter";
import LanguageFilter from "./LanguageFilter";
import SessionTypeFilter from "./SessionTypeFilter";
import GenderFilter from "./GenderFilter";
import FreeSessionFilter from "./FreeSessionFilter";
import ServiceTrackFilter from "./ServiceTrackFilter";
import ThirdPartyRatingFilter from "./ThirdPartyRatingFilter";

interface FiltersSidebarProps {
  filters: {
    serviceCategories: string[];
    effectiveDomain: string;
    serviceSubcategory: string;
    distance: string;
    date: Date | undefined;
    timeSlot: string;
    sessionType: string | string[];
    genders: string[];
    language: string;
    issueCategory: string;
    showFreeSession: boolean;
    serviceTracks: string[];
    includeThirdPartyRating?: boolean;
    therapyModalities: string[];
  };
  setFilters: (filters: any) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  userLocation: { lat: number; lng: number } | null;
  userAddress: string | null;
  locationError: string | null;
  isGettingLocation: boolean;
  isLoadingAddress: boolean;
  requestLocationAccess: () => void;
  timeSlots: string[];
  isDateDisabled: (date: Date) => boolean;
  handleCategoryChange: (categoryName: string) => void;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  timezoneDisplay?: string | null;
  isApproximateLocation?: boolean;
  setManualLocation?: (lat: number, lng: number, address: string) => void;
  isGeolocationPolicyBlocked?: () => boolean;
}

const FiltersSidebar = ({
  filters,
  setFilters,
  searchQuery,
  onSearchChange,
  userLocation,
  userAddress,
  locationError,
  isGettingLocation,
  isLoadingAddress,
  requestLocationAccess,
  timeSlots,
  isDateDisabled,
  handleCategoryChange,
  city,
  state,
  country,
  timezoneDisplay,
  isApproximateLocation,
  setManualLocation,
  isGeolocationPolicyBlocked
}: FiltersSidebarProps) => {
  const { t } = useTranslation('practitioners');

  return (
    <Card className="sticky top-24">
      <CardContent className="p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-medium flex items-center">
            <SlidersHorizontal className="mr-2 h-5 w-5 text-amber-600" /> {t('filters')}
          </h2>
        </div>

        <div className="space-y-6">
          {/* Search Input */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder={t('searchPractitioners')}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <ServiceCategoryFilter 
            selectedCategories={filters.serviceCategories}
            onCategoryChange={handleCategoryChange}
          />

          <DomainFilter
            selectedDomain={filters.effectiveDomain}
            onDomainChange={(domain) => setFilters({...filters, effectiveDomain: domain})}
          />

          <SubcategoryFilter
            selectedDomain={filters.effectiveDomain}
            selectedSubcategory={filters.serviceSubcategory}
            onSubcategoryChange={(subcategory) => setFilters({...filters, serviceSubcategory: subcategory})}
            onDomainChange={(domain) => setFilters({...filters, effectiveDomain: domain})}
          />

          <TherapyModalityFilter
            selectedModalities={filters.therapyModalities}
            onModalitiesChange={(ids) => setFilters({...filters, therapyModalities: ids})}
            currentStage={filters.serviceCategories[0]}
            currentDomain={filters.effectiveDomain}
            currentSubcategory={filters.serviceSubcategory}
          />

          <IssueCategoryFilter
          selectedCategory={filters.issueCategory}
          onCategoryChange={(category) => setFilters({...filters, issueCategory: category})}
        />

          <LanguageFilter
            selectedLanguage={filters.language}
            onLanguageChange={(language) => setFilters({...filters, language})}
          />

          <SessionTypeFilter 
            sessionType={filters.sessionType}
            onSessionTypeChange={(sessionType) => setFilters({...filters, sessionType})}
          />

          <GenderFilter
            selectedGenders={filters.genders}
            onGenderChange={(genders) => setFilters({...filters, genders})}
          />

          <FreeSessionFilter
            showFreeSession={filters.showFreeSession}
            onToggle={(checked) => setFilters({...filters, showFreeSession: checked})}
          />

          <ServiceTrackFilter
            selectedTracks={filters.serviceTracks}
            onTrackChange={(serviceTracks) => setFilters({...filters, serviceTracks})}
          />

          <ThirdPartyRatingFilter
            includeThirdPartyRating={filters.includeThirdPartyRating || true}
            onToggle={(checked) => setFilters({...filters, includeThirdPartyRating: checked})}
          />
        </div>

        <div className="mt-8">
          <Button 
            variant="outline" 
            className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 transition-colors font-medium py-3"
            onClick={() => {
              // Clear localStorage cache
              localStorage.removeItem('practitioner_filters');
              
              setFilters({
                serviceCategories: ["all_types"],
                effectiveDomain: "all_domains",
                serviceSubcategory: "all_subcategories",
                distance: "any_distance",
                date: undefined,
                timeSlot: "any_time",
                sessionType: ['virtual', 'in_person'],
                genders: ['male', 'female', 'other'],
                language: 'all',
                issueCategory: 'all_categories',
                showFreeSession: false,
                serviceTracks: ['clinical', 'wellness'],
                includeThirdPartyRating: true,
                therapyModalities: [],
              });
              onSearchChange("");
            }}
          >
            {t('clearAllFilters')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FiltersSidebar;
